import React, { useState } from "react";
import { Field } from "formik";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import OperatingHoursPopup from "./OperatingHoursPopup";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminMetadataText,
  AdminTableBody,
  AdminMainText,
} from "../../sections/AdminDashboard/shared/adminUx";

const OperatingHoursWidget = ({ setFieldValue, values, errors }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(0);

  // Define fields with estimated completion times
  const fields = [
    // Temporarily removed time zone and response time
    // { name: "Time zone", key: "timeZone", estimatedTime: "1 min", required: false },
    { name: "Weekly schedule", key: "weeklySchedule", estimatedTime: "5 min", required: false, popupStep: 1 },
    // { name: "Response time", key: "responseTime", estimatedTime: "1 min", required: false }
  ];

  // Sample data for demonstration
  const sampleData = {
    // All fields left empty intentionally
  };

  // Merge sample data with actual values (use sample data when form values are empty)
  const displayValues = {
    timeZone: values.timeZone || sampleData.timeZone || "",
    weeklySchedule: "Configured", // This will be determined by checking if any day is configured
    responseTime: values.responseTime || sampleData.responseTime || ""
  };

  // Check if weekly schedule is configured
  const isWeeklyScheduleConfigured = () => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'pholiday'];
    const isConfigured = days.some(day => {
      const isOpen = values[`${day}Open`];
      const hasStartTime = values[`${day}Start`] && values[`${day}Start`].trim() !== "";
      const hasEndTime = values[`${day}End`] && values[`${day}End`].trim() !== "";
      return (isOpen === true || isOpen === "true") && hasStartTime && hasEndTime;
    });
    
    return isConfigured;
  };

  // Get configured days for display
  const getConfiguredDays = () => {
    const days = [
      { key: 'monday', label: 'Mon' },
      { key: 'tuesday', label: 'Tue' },
      { key: 'wednesday', label: 'Wed' },
      { key: 'thursday', label: 'Thu' },
      { key: 'friday', label: 'Fri' },
      { key: 'saturday', label: 'Sat' },
      { key: 'sunday', label: 'Sun' },
      { key: 'pholiday', label: 'P. Holiday' }
    ];
    
    const configuredDays = days.filter(day => {
      const isOpen = values[`${day.key}Open`];
      const hasStartTime = values[`${day.key}Start`] && values[`${day.key}Start`].trim() !== "";
      const hasEndTime = values[`${day.key}End`] && values[`${day.key}End`].trim() !== "";
      
      return (isOpen === true || isOpen === "true") && hasStartTime && hasEndTime;
    });
    return configuredDays.map(day => day.label);
  };

  // Calculate completion percentage
  const calculateProgress = () => {
    const completedFields = fields.filter(field => {
      if (field.key === 'weeklySchedule') {
        return isWeeklyScheduleConfigured();
      }
      const hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
      return hasValue;
    }).length;
    
    return Math.round((completedFields / fields.length) * 100);
  };

  // Calculate estimated time for remaining fields
  const calculateEstimatedTime = () => {
    const remainingFields = fields.filter(field => {
      let hasValue = false;
      if (field.key === 'weeklySchedule') {
        hasValue = displayValues[field.key] && Object.keys(displayValues[field.key]).length > 0;
      } else {
        hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
      }
      return !hasValue;
    });
    
    const totalMinutes = remainingFields.reduce((total, field) => {
      const timeStr = field.estimatedTime;
      const minutes = parseInt(timeStr.replace(' min', ''));
      return total + minutes;
    }, 0);
    
    return totalMinutes > 0 ? `Estimated ${totalMinutes} min` : 'All Completed';
  };

  const progress = calculateProgress();
  const estimatedTime = calculateEstimatedTime();

  return (
    <>
    <DashboardMainContainer className="masonry-item w-[500px] h-auto account-widget">
      <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
        <AdminPanelTitle className="text-offblack">Business Hours</AdminPanelTitle>
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 bg-lightgray rounded-full overflow-hidden">
            <div 
              className="h-full bg-freshgreen transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
            <AdminMetric size="micro" className="text-offblack tabular-nums">
              {progress}%
            </AdminMetric>
        </div>
      </div>
      
        <div className="px-4 py-4">
          {/* Fields List */}
          <div className="space-y-0">
            {fields.map((field, index) => {
              let hasValue = false;
              if (field.key === 'weeklySchedule') {
                hasValue = isWeeklyScheduleConfigured();
              } else {
                hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
              }
              const isCompleted = hasValue; // Only completed if it actually has a value
              
              return (
                <div key={field.key} className="flex items-center justify-between">
                    {progress === 100 ? (
                      <div 
                        className={`group flex items-center w-full py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2`}
                        onClick={() => {
                          setSelectedFieldIndex(field.popupStep || index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <AdminTableBody emphasis className="w-56 text-offblack group-hover:text-primaryblue">
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className="text-offblack group-hover:text-primaryblue truncate">
                          {isCompleted ? (field.key === 'weeklySchedule' ? getConfiguredDays().join(", ") : displayValues[field.key]) : `Estimated ${field.estimatedTime}`}
                        </AdminMainText>
                      </div>
                    ) : (
                    <>
                      <div 
                        className={`group flex items-center py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2 ${index < fields.length - 1 ? 'border-b border-transparent' : ''}`}
                        onClick={() => {
                          setSelectedFieldIndex(field.popupStep || index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <div className={`w-2 h-2 rounded-full mr-3 ${isCompleted ? 'bg-freshgreen' : 'bg-mediumgray'}`}></div>
                        <AdminTableBody emphasis className={`w-40 ${isCompleted ? 'text-offblack group-hover:text-primaryblue' : 'text-mediumgray group-hover:text-primaryblue'}`}>
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className={`text-offblack group-hover:text-primaryblue truncate max-w-[200px]`}>
                          {isCompleted ? (field.key === 'weeklySchedule' ? getConfiguredDays().join(", ") : displayValues[field.key]) : ''}
                        </AdminMainText>
                      </div>
                      <AdminMetadataText className="text-mediumgray">
                        {isCompleted ? 'Completed' : 'Start'}
                      </AdminMetadataText>
                    </>
                  )}
                </div>
              );
            })}
        </div>

      </div>
    </DashboardMainContainer>

      {/* Popup for editing */}
      {isPopupOpen && (
        <OperatingHoursPopup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          setFieldValue={setFieldValue}
          values={values}
          errors={errors}
          initialStep={selectedFieldIndex}
        />
      )}
    </>
  );
};

export default OperatingHoursWidget;
